import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import {
  borderGrey,
  borderPrimary,
  darkerBG,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
} from '../../styling/stylingHelper';
import { FrontendStaffUser, updateStaffRestaurantAccess } from '../../services/staff/staffService';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { RevenueSourceType } from '../../types/RevenueSourceType';
import { RestaurantSectionType } from '../../types/RestaurantSectionType';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../ui/InventorySlideOver';

interface Props {
  isOpen: boolean;
  staff: FrontendStaffUser | null;
  restaurantId: string | null;
  onClose: () => void;
  onUpdated: () => void;
}

type AccessType = 'all' | 'food' | 'drinks';

const ManageStaffAccessModal: React.FC<Props> = ({ isOpen, staff, restaurantId, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const restaurants = useRestaurantStore((s) => s.restaurants);
  const restaurantSectionsMap = useRestaurantStore((s) => s.restaurantSections);
  const restaurantRevenueSourcesMap = useRestaurantStore((s) => s.restaurantRevenueSources);

  const restaurantSections = React.useMemo(() => {
    if (!restaurantId) return [];
    return restaurantSectionsMap[restaurantId] || [];
  }, [restaurantId, restaurantSectionsMap]);

  const restaurantRevenueSources = React.useMemo(() => {
    if (!restaurantId) return [];
    return restaurantRevenueSourcesMap[restaurantId] || [];
  }, [restaurantId, restaurantRevenueSourcesMap]);

  const restaurant = React.useMemo(() => {
    if (!restaurantId || !restaurants || restaurants.length === 0) return null;
    return restaurants.find((r) => r.id === restaurantId) || null;
  }, [restaurants, restaurantId]);

  const currentAccess = React.useMemo(() => {
    if (!staff || !restaurantId || !staff.restaurant_access) return undefined;
    return staff.restaurant_access[restaurantId];
  }, [staff, restaurantId]);

  const [selectedAccessType, setSelectedAccessType] = useState<AccessType>('all');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deriveAccessTypeFromRestrictions = React.useCallback((): AccessType => {
    if (!currentAccess) return 'all';

    const sectionIds = Array.isArray(currentAccess.allowed_section_ids) ? currentAccess.allowed_section_ids : [];
    const revenueTypes = Array.isArray(currentAccess.allowed_revenue_source_types)
      ? currentAccess.allowed_revenue_source_types
      : [];

    if (sectionIds.length === 0 && revenueTypes.length === 0) return 'all';

    const sections = Array.isArray(restaurantSections) ? restaurantSections : [];
    const foodSectionIds = sections
      .filter((s) => s?.restaurantSectionType === RestaurantSectionType.FOOD)
      .map((s) => s.id);
    const drinksSectionIds = sections
      .filter((s) => s?.restaurantSectionType === RestaurantSectionType.DRINKS)
      .map((s) => s.id);

    const allSectionsAreFood =
      sectionIds.length > 0 &&
      sectionIds.every((id) => foodSectionIds.includes(id)) &&
      !sectionIds.some((id) => drinksSectionIds.includes(id));

    const allSectionsAreDrinks =
      sectionIds.length > 0 &&
      sectionIds.every((id) => drinksSectionIds.includes(id)) &&
      !sectionIds.some((id) => foodSectionIds.includes(id));

    const relevantRevenueTypes = revenueTypes.filter(
      (rt) => rt === RevenueSourceType.FOOD || rt === RevenueSourceType.DRINKS
    );

    const hasOnlyFoodRevenue =
      relevantRevenueTypes.length > 0 &&
      relevantRevenueTypes.includes(RevenueSourceType.FOOD) &&
      !relevantRevenueTypes.includes(RevenueSourceType.DRINKS);

    const hasOnlyDrinksRevenue =
      relevantRevenueTypes.length > 0 &&
      relevantRevenueTypes.includes(RevenueSourceType.DRINKS) &&
      !relevantRevenueTypes.includes(RevenueSourceType.FOOD);

    if (allSectionsAreFood && (revenueTypes.length === 0 || hasOnlyFoodRevenue)) return 'food';
    if (allSectionsAreDrinks && (revenueTypes.length === 0 || hasOnlyDrinksRevenue)) return 'drinks';
    return 'all';
  }, [currentAccess, restaurantSections]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedAccessType('all');
      setError(null);
      return;
    }
    if (staff && restaurantId) {
      setSelectedAccessType(deriveAccessTypeFromRestrictions());
      setError(null);
    }
  }, [isOpen, staff, restaurantId, deriveAccessTypeFromRestrictions]);

  const deriveRestrictionsFromAccessType = React.useCallback(
    (accessType: AccessType): { sectionIds: string[] | null; revenueTypes: RevenueSourceType[] | null } => {
      if (accessType === 'all') return { sectionIds: null, revenueTypes: null };

      const sections = Array.isArray(restaurantSections) ? restaurantSections : [];
      const revenueSources = Array.isArray(restaurantRevenueSources) ? restaurantRevenueSources : [];

      const sectionType = accessType === 'food' ? RestaurantSectionType.FOOD : RestaurantSectionType.DRINKS;
      const revenueType = accessType === 'food' ? RevenueSourceType.FOOD : RevenueSourceType.DRINKS;

      const matchingSections = sections.filter((s) => s?.restaurantSectionType === sectionType);
      const matchingRevenueSources = revenueSources.filter((rs) => rs?.revenueSourceType === revenueType);

      // A missing side stays null (= unrestricted) rather than an empty allow-list,
      // which the server would read as "access to nothing".
      return {
        sectionIds: matchingSections.length > 0 ? matchingSections.map((s) => s.id) : null,
        revenueTypes: matchingRevenueSources.length > 0 ? [revenueType] : null,
      };
    },
    [restaurantSections, restaurantRevenueSources]
  );

  const handleSave = async () => {
    if (!staff || !restaurantId) return;
    setSaving(true);
    setError(null);
    try {
      const { sectionIds, revenueTypes } = deriveRestrictionsFromAccessType(selectedAccessType);
      await updateStaffRestaurantAccess(staff.id, restaurantId, sectionIds, revenueTypes);
      onUpdated();
    } catch (err) {
      console.error(err);
      setError(t('staff.errorUpdateAccess'));
    } finally {
      setSaving(false);
    }
  };

  const accessOptions: { value: AccessType; label: string }[] = [
    { value: 'all', label: t('staff.allAccess') },
    { value: 'food', label: t('staff.foodOnly') },
    { value: 'drinks', label: t('staff.drinksOnly') },
  ];

  const ready = Boolean(staff && restaurantId && restaurant);

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('staff.manageAccess')}
      subtitle={staff?.email || ''}
      closeLabel={t('common.close')}
      footer={
        <>
          <button type="button" onClick={onClose} className={slideOverCancelClass} disabled={saving}>
            {t('staff.cancel')}
          </button>
          <button type="button" onClick={handleSave} className={slideOverPrimaryClass} disabled={saving || !ready}>
            {saving ? t('staff.saving') : t('staff.save')}
          </button>
        </>
      }
    >
      {!ready ? (
        <p className={cn(smallTextSize, secondaryText)}>{t('common.loading')}</p>
      ) : (
        <>
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <p className={cn('text-[16px] leading-7', semiBoldText, primaryText)}>{t('staff.accessType')}</p>
            <p className={cn('pt-3 text-[14px] leading-6 font-normal', tertiaryText)}>
              {t('staff.accessTypeDescription')}
            </p>

            <div className={cn('mt-6 space-y-3 p-4 rounded-lg border', borderPrimary)}>
              {accessOptions.map((opt) => {
                const selected = selectedAccessType === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={cn(
                      'border flex items-center justify-between gap-3 p-[11px] rounded-lg cursor-pointer transition-colors',
                      selected
                        ? 'bg-darkGreenColor/10 dark:bg-darkGreenColor-dark/10 border-darkGreenColor dark:border-darkGreenColor-dark'
                        : cn(borderGrey, darkerBG)
                    )}
                  >
                    <span className={cn('text-[16px] leading-5', semiBoldText, primaryText)}>{opt.label}</span>
                    <span className="relative flex items-center justify-center w-5 h-5 shrink-0">
                      <input
                        type="radio"
                        name="accessType"
                        value={opt.value}
                        checked={selected}
                        onChange={() => setSelectedAccessType(opt.value)}
                        className="peer sr-only"
                      />
                      <span
                        className={cn(
                          'w-5 h-5 rounded-full bg-background dark:bg-background-dark transition-all duration-150',
                          selected
                            ? 'border-[6px] border-darkGreenColor dark:border-darkGreenColor-dark'
                            : cn('border-2', borderPrimary)
                        )}
                      />
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}
    </InventorySlideOver>
  );
};

export default ManageStaffAccessModal;
